/**
 * Zod schemas for the 16 care-flow component types (Sprint 4.3.4, PROD-653).
 *
 * Each folder gets its own strict Zod schema. The JSON editor uses the
 * matching schema in `validateComponentJson` to gate Save: an invalid
 * Form-mode submission or an invalid Raw-JSON submission is refused
 * with a structured error list.
 *
 * HC9 strict registry: `componentJsonSchemaByFolder[folder]` is the
 * single source of truth for validation. The publish workflow refuses
 * to emit any component instance that fails its schema.
 *
 * HC5: zero em / en dashes anywhere.
 */
import { z } from 'zod';
import { CARE_FLOW_FOLDERS, type CareFlowFolder } from './types';

const baseShape = {
  id: z.string().min(1, 'id is required'),
  label: z.string().min(1, 'label is required'),
  version: z.string().min(1, 'version is required'),
  lastModified: z.string().min(1, 'lastModified is required'),
  usedByPathwayIds: z.array(z.string().min(1)).optional(),
};

export const actionSchema = z.object({
  ...baseShape,
  category: z.enum(['user', 'system']),
  formId: z.string().min(1).optional(),
  apiCallId: z.string().min(1).optional(),
  stepId: z.string().min(1).optional(),
});

export const apiCallSchema = z.object({
  ...baseShape,
  endpoint: z.string().min(1, 'endpoint is required'),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  description: z.string().optional(),
});

export const dataPointDefinitionSchema = z.object({
  ...baseShape,
  valueType: z.enum(['string', 'number', 'boolean', 'date', 'codeable-concept']),
  codingSystem: z.string().min(1).optional(),
  code: z.string().min(1).optional(),
});

export const formSchema = z.object({
  ...baseShape,
  questionIds: z.array(z.string().min(1)),
  stakeholderId: z.string().min(1).optional(),
});

export const labelSchema = z.object({
  ...baseShape,
  category: z.string().min(1).optional(),
});

export const messageSchema = z.object({
  ...baseShape,
  channel: z.enum(['email', 'sms', 'in-app', 'voice']),
  body: z.string().min(1, 'body is required'),
  recipientStakeholderId: z.string().min(1).optional(),
});

export const pathwaySchema = z.object({
  ...baseShape,
  trackIds: z.array(z.string().min(1)),
  triggerIds: z.array(z.string().min(1)).optional(),
  tenantId: z.string().min(1, 'tenantId is required'),
});

export const questionSchema = z.object({
  ...baseShape,
  questionType: z.enum([
    'short-text',
    'long-text',
    'single-choice',
    'multi-choice',
    'number',
    'date',
    'boolean',
  ]),
  required: z.boolean().optional(),
  options: z.array(z.string().min(1)).optional(),
});

export const ruleSchema = z.object({
  ...baseShape,
  expression: z.string().min(1, 'expression is required'),
});

export const settingSchema = z.object({
  ...baseShape,
  key: z.string().min(1, 'key is required'),
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
});

export const stakeholderSchema = z.object({
  ...baseShape,
  role: z.enum(['patient', 'practitioner', 'caregiver', 'system']),
});

export const stepSchema = z.object({
  ...baseShape,
  trackId: z.string().min(1, 'trackId is required'),
  actionIds: z.array(z.string().min(1)),
  isDecision: z.boolean().optional(),
});

export const timingSchema = z.object({
  ...baseShape,
  kind: z.enum(['delay', 'schedule', 'recurring']),
  expression: z.string().min(1, 'expression is required'),
});

export const trackSchema = z.object({
  ...baseShape,
  pathwayId: z.string().min(1, 'pathwayId is required'),
  stepIds: z.array(z.string().min(1)),
  triggerType: z.string().min(1).optional(),
  triggerFilter: z.string().min(1).optional(),
});

export const transitionSchema = z.object({
  ...baseShape,
  fromStepId: z.string().min(1, 'fromStepId is required'),
  toStepId: z.string().min(1, 'toStepId is required'),
  ruleId: z.string().min(1).optional(),
});

export const triggerSchema = z.object({
  ...baseShape,
  kind: z.enum(['manual', 'scheduled', 'api', 'patient-event']),
  configuration: z.record(z.unknown()).optional(),
});

export const componentJsonSchemaByFolder: Record<CareFlowFolder, z.ZodTypeAny> = {
  actions: actionSchema,
  api_calls: apiCallSchema,
  data_point_definitions: dataPointDefinitionSchema,
  forms: formSchema,
  labels: labelSchema,
  messages: messageSchema,
  pathways: pathwaySchema,
  questions: questionSchema,
  rules: ruleSchema,
  settings: settingSchema,
  stakeholders: stakeholderSchema,
  steps: stepSchema,
  timings: timingSchema,
  tracks: trackSchema,
  transitions: transitionSchema,
  triggers: triggerSchema,
};

export interface ValidationFailure {
  ok: false;
  errors: ReadonlyArray<{ path: string; message: string }>;
}

export interface ValidationSuccess<T> {
  ok: true;
  value: T;
}

export type ValidationResult<T> = ValidationSuccess<T> | ValidationFailure;

export function validateComponentJson(
  folder: CareFlowFolder,
  candidate: unknown,
): ValidationResult<unknown> {
  if (!CARE_FLOW_FOLDERS.includes(folder)) {
    return {
      ok: false,
      errors: [{ path: '$', message: `unknown folder: ${folder}` }],
    };
  }
  const schema = componentJsonSchemaByFolder[folder];
  const parsed = schema.safeParse(candidate);
  if (parsed.success) {
    return { ok: true, value: parsed.data };
  }
  return {
    ok: false,
    errors: parsed.error.errors.map((e) => ({
      path: e.path.length === 0 ? '$' : `$.${e.path.join('.')}`,
      message: e.message,
    })),
  };
}
